const NO_BREAK_SPACES_PATTERN = /[\u00a0\u202f]/g
const CLOCK_TEXT_PATTERN = /^(\d{1,2})(?:[:.h]?(\d{2}))?\s*(?:([ap])\.?\s*(?:m\.?)?)?$/

// Times of day as the builder's time fields type, show and store them. The
// schedule model imports from here, so this module imports nothing from it.

// A run time as the rule stores it: "HH:MM" on a 24-hour clock.
export const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
export const pad2 = (value) => String(value).padStart(2, '0')

/**
 * "HH:MM" (24-hour) for a typed time: `9`, `930`, `9:30`, `21.30`, `9:30pm`,
 * `937p`, `9 pm` and `9:30 p.m.` all work. '' when blank and `allowBlank`;
 * undefined while the text is not a time yet.
 */
export function parseTimeText(text, { allowBlank = false } = {}) {
  const trimmed = String(text ?? '').replace(NO_BREAK_SPACES_PATTERN, ' ').trim().toLocaleLowerCase()
  if (trimmed === '') return allowBlank ? '' : undefined
  const match = trimmed.match(CLOCK_TEXT_PATTERN)
  if (!match) return undefined
  let hour = Number(match[1])
  const minute = Number(match[2] ?? 0)
  const meridiem = match[3]
  if (minute > 59) return undefined
  if (meridiem) {
    if (hour < 1 || hour > 12) return undefined
    if (meridiem === 'p' && hour < 12) hour += 12
    if (meridiem === 'a' && hour === 12) hour = 0
  }
  if (hour > 23) return undefined
  return `${pad2(hour)}:${pad2(minute)}`
}

/**
 * A clock formatter for `locale` (the operator's, `navigator.language`,
 * when omitted): 12- or 24-hour as that locale writes times. The browser's
 * language rather than Intl's own default, so a test run reads the same
 * clock (jsdom's en-US) whatever the machine's locale.
 */
export function timeFormatter(locale = typeof navigator === 'undefined' ? undefined : navigator.language) {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
}

let localFormatter = null

/**
 * "HH:MM" written the way the operator's locale writes a time ("7:00 PM" or
 * "19:00"); '' for anything else. Narrow and non-breaking spaces become plain
 * ones so the text can be typed back.
 */
export function formatTimeOfDay(value, formatter) {
  if (!TIME_PATTERN.test(String(value ?? ''))) return ''
  const [hour, minute] = value.split(':').map(Number)
  localFormatter ??= timeFormatter()
  return (formatter || localFormatter).format(Date.UTC(2000, 0, 1, hour, minute)).replace(NO_BREAK_SPACES_PATTERN, ' ')
}
